import { buildReminderContent, nextDeparture } from './tripAlertLogic';
import type { TripWatch } from './types';
import { DEFAULT_THRESHOLDS } from './types';

const KEY = 'ontheroute_trip_watches_v1';
const FIRED_KEY = 'ontheroute_trip_watches_fired_v1';

export function loadWatches(): TripWatch[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveWatches(watches: TripWatch[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(watches));
  } catch {
    // ignore
  }
}

export function loadFiredIds(): Set<string> {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    return new Set(Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw || '[]') : []);
  } catch {
    return new Set();
  }
}

export function saveFiredIds(ids: Set<string>): void {
  try {
    localStorage.setItem(FIRED_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

export async function ensureNotificationPermission(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

export function notifyBrowser(title: string, body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body });
  } catch {
    // ignore
  }
}

export function formatters(imperial: boolean) {
  return {
    time: (ms: number) =>
      new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
    wind: (kmh: number) => (imperial ? `${Math.round(kmh * 0.621371)} mph` : `${Math.round(kmh)} km/h`),
  };
}

export function tickReminders(now = Date.now(), imperial = true): string[] {
  const watches = loadWatches();
  const fired = loadFiredIds();
  const f = formatters(imperial);
  const shown: string[] = [];
  for (const watch of watches) {
    if (!watch.reminderEnabled) continue;
    const dep = nextDeparture(watch, now);
    if (dep == null) continue;
    const fireTargetMs = dep - Math.max(0, watch.reminderLeadMin) * 60_000;
    if (now < fireTargetMs || now > dep) continue;
    const id = `${watch.id}:${dep}`;
    if (fired.has(id)) continue;
    const content = buildReminderContent(watch, dep, f);
    notifyBrowser(content.title, content.body);
    fired.add(id);
    shown.push(content.title);
  }
  saveFiredIds(fired);
  return shown;
}

export function watchFromCurrentTrip(params: {
  name: string;
  destinationName: string;
  departAt: number;
  durationSec: number;
  reminderLeadMin: number;
  imperial: boolean;
}): TripWatch {
  const now = Date.now();
  return {
    id: `watch-${now}`,
    name: params.name,
    destinationName: params.destinationName,
    points: [],
    durationSec: params.durationSec,
    recurrence: { kind: 'once' },
    departAt: params.departAt,
    reminderEnabled: true,
    reminderLeadMin: params.reminderLeadMin,
    alertsEnabled: false,
    thresholds: DEFAULT_THRESHOLDS,
    imperial: params.imperial,
    createdAt: now,
    updatedAt: now,
  };
}
